/**
 * Internal-visitor marker, shared by the marketing site and the app SPA.
 *
 * The problem it solves: the pre-signup funnel and the acquisition funnels are
 * built on rows that carry no person, so the admin exclusion list (a list of
 * handles) cannot reach a teammate who opens the landing page, taps Start and
 * never signs in. Their page loads, scroll depth and entry taps land in the
 * product numbers exactly like a stranger's, and nothing in the row says
 * otherwise. Guessing by IP or user agent would be worse than counting them.
 *
 * So we mark the browser instead. Normally nobody does this by hand: signing in
 * to an internal account marks the browser server-side and the mark outlives
 * the session (see #edge/auth/internal-user.js), which is what covers the
 * signed-out browsing that started this. This script is the manual override for
 * the browser that has not signed in yet — open any page with `?internal=1` and
 * it drops the same long-lived cookie. Either way the server then stamps
 * `internal: true` on every beacon and every server-written funnel row from this
 * browser (see readInternalVisitorCookie), and the funnels drop them.
 * `?internal=0` clears it — a shared or handed-down device must be able to
 * become a normal visitor again, and it clears the server-set mark too.
 *
 * Deliberately a cookie and not localStorage (where the consent choice lives):
 * the rows that matter most are written server-side — `landing.view` is the
 * door's denominator — and no page script can mark those. The cookie carries no
 * id, only the flag, and it is read by our own origin only.
 *
 * The marker is also what the product itself means by "internal", not only the
 * funnels: a marked browser is exempt from the signed-out guest limits (the
 * free-telling budget and the unclaimed caps), so the team can exercise the
 * signed-out call and chat entrances as often as testing needs. One marker,
 * read by analytics and by the gates separately — see readInternalVisitorCookie
 * on the server.
 *
 * Not gated on cookie consent, and not a tracker: it identifies nobody and is
 * set only by an explicit act (opening a URL only the team is given). The URL is
 * stripped of the parameter afterwards so a marked link pasted into a group chat
 * cannot quietly mark someone else who follows it — they would have to receive
 * the parameter itself.
 */

const COOKIE_NAME = 'ori_internal';
// 400 days — the cap Chrome enforces for a Set-Cookie Max-Age anyway, and the
// same horizon the guest-trial cookie uses. A marker that expires in a week
// silently stops working and the funnel drifts back without anyone noticing.
const MAX_AGE_SECONDS = 400 * 24 * 60 * 60;
const PARAM = 'internal';

function writeCookie(value: string, maxAgeSeconds: number): void {
  try {
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${COOKIE_NAME}=${value}; Path=/; Max-Age=${maxAgeSeconds}; SameSite=Lax${secure}`;
  } catch {
    // Cookies blocked — nothing to do. The visitor simply stays counted, which
    // is the honest default for a marker that could not be stored.
  }
}

/** True when this browser already carries the marker. */
export function isInternalVisitor(): boolean {
  try {
    return document.cookie.split(';').some((part) => part.trim() === `${COOKIE_NAME}=1`);
  } catch {
    return false;
  }
}

/**
 * Apply `?internal=1` / `?internal=0` if present, then strip the parameter from
 * the visible URL. Safe to call on every page load; a no-op without the param.
 *
 * Returns whether the browser carries the marker AFTER applying the URL, so a
 * caller can log it — though nothing needs to: the server reads the cookie.
 */
export function syncInternalVisitorMarker(): boolean {
  try {
    const url = new URL(window.location.href);
    const raw = url.searchParams.get(PARAM);
    if (raw === '1' || raw === '0') {
      // '0' clears by expiring immediately rather than by deleting a name the
      // browser may hold on more than one path.
      writeCookie(raw === '1' ? '1' : '', raw === '1' ? MAX_AGE_SECONDS : 0);
      url.searchParams.delete(PARAM);
      const qs = url.searchParams.toString();
      window.history.replaceState(
        window.history.state,
        '',
        `${url.pathname}${qs ? `?${qs}` : ''}${url.hash}`,
      );
    }
  } catch {
    // Malformed URL / no history API — leave whatever marker already exists.
  }
  return isInternalVisitor();
}
